import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const UserProgressContext = createContext();

export const useUserProgress = () => {
  const context = useContext(UserProgressContext);
  if (!context) {
    throw new Error('useUserProgress must be used within UserProgressProvider');
  }
  return context;
};

export const UserProgressProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [readModules, setReadModules] = useState([]);
  const [highlightedCards, setHighlightedCards] = useState([]);
  const [highlightsWithContent, setHighlightsWithContent] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch user progress on mount and when user changes
  useEffect(() => {
    if (currentUser && !currentUser.isAnonymous) {
      fetchUserProgress();
    } else {
      // For guest users, load from localStorage
      const guestReadModules = JSON.parse(localStorage.getItem('guestReadModules') || '[]');
      const guestHighlightedCards = JSON.parse(localStorage.getItem('guestHighlightedCards') || '[]');
      const guestHighlightsWithContent = JSON.parse(localStorage.getItem('guestHighlightsWithContent') || '[]');
      setReadModules(guestReadModules);
      setHighlightedCards(guestHighlightedCards);
      setHighlightsWithContent(guestHighlightsWithContent);
      setLoading(false);
    }
  }, [currentUser]);

  const fetchUserProgress = async () => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/user-progress/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReadModules(data.read_modules || []);
        setHighlightedCards(data.highlighted_cards || []);
        setHighlightsWithContent(data.highlights_with_content || []);
      }
    } catch (error) {
      console.error('Error fetching user progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const markModuleAsRead = async (moduleRoute) => {
    if (!currentUser) return;

    // For guest users, save to localStorage
    if (currentUser.isAnonymous) {
      const updatedModules = [...new Set([...readModules, moduleRoute])];
      setReadModules(updatedModules);
      localStorage.setItem('guestReadModules', JSON.stringify(updatedModules));
      return;
    }

    // For registered users, save to backend
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/user-progress/mark-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ module_route: moduleRoute })
      });

      if (response.ok) {
        setReadModules(prev => [...new Set([...prev, moduleRoute])]);
      }
    } catch (error) {
      console.error('Error marking module as read:', error);
    }
  };

  const toggleHighlight = async (moduleRoute, cardId, content = '') => {
    if (!currentUser) return;

    const cardIdentifier = `${moduleRoute}:${cardId}`;

    // For guest users, save to localStorage
    if (currentUser.isAnonymous) {
      const isCurrentlyHighlighted = highlightedCards.includes(cardIdentifier);
      let updatedCards;
      let updatedContent;
      
      if (isCurrentlyHighlighted) {
        // Remove highlight
        updatedCards = highlightedCards.filter(id => id !== cardIdentifier);
        // Match by BOTH cardId AND moduleRoute to avoid removing wrong items
        updatedContent = highlightsWithContent.filter(h => 
          !(h.cardId === cardId && h.moduleRoute === moduleRoute)
        );
      } else {
        // Add highlight
        updatedCards = [...new Set([...highlightedCards, cardIdentifier])];
        updatedContent = [...highlightsWithContent, {
          cardId,
          content,
          moduleRoute,
          timestamp: new Date().toISOString()
        }];
      }
      
      setHighlightedCards(updatedCards);
      setHighlightsWithContent(updatedContent);
      localStorage.setItem('guestHighlightedCards', JSON.stringify(updatedCards));
      localStorage.setItem('guestHighlightsWithContent', JSON.stringify(updatedContent));
      return;
    }

    // For registered users, save to backend
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/user-progress/toggle-highlight`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          module_route: moduleRoute,
          card_id: cardId,
          content: content
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.highlighted) {
          // Add highlight
          setHighlightedCards(prev => [...new Set([...prev, cardIdentifier])]);
          setHighlightsWithContent(prev => [...prev, {
            cardId,
            content,
            moduleRoute,
            timestamp: new Date().toISOString()
          }]);
        } else {
          // Remove highlight - match by BOTH cardId AND moduleRoute
          setHighlightedCards(prev => prev.filter(id => id !== cardIdentifier));
          setHighlightsWithContent(prev => prev.filter(h => 
            !(h.cardId === cardId && h.moduleRoute === moduleRoute)
          ));
        }
      }
    } catch (error) {
      console.error('Error toggling highlight:', error);
    }
  };

  const isModuleRead = (moduleRoute) => {
    return readModules.includes(moduleRoute);
  };

  const isCardHighlighted = (moduleRoute, cardId) => {
    const cardIdentifier = `${moduleRoute}:${cardId}`;
    return highlightedCards.includes(cardIdentifier);
  };

  const clearAllHighlights = async () => {
    if (!currentUser) return;

    // For guest users, clear from localStorage
    if (currentUser.isAnonymous) {
      setHighlightedCards([]);
      setHighlightsWithContent([]);
      localStorage.setItem('guestHighlightedCards', JSON.stringify([]));
      localStorage.setItem('guestHighlightsWithContent', JSON.stringify([]));
      return;
    }

    // For registered users, clear from backend
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/user-progress/clear-highlights`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setHighlightedCards([]);
        setHighlightsWithContent([]);
      }
    } catch (error) {
      console.error('Error clearing highlights:', error);
    }
  };

  const value = {
    readModules,
    highlightedCards,
    highlightsWithContent,
    loading,
    markModuleAsRead,
    toggleHighlight,
    isModuleRead,
    isCardHighlighted,
    clearAllHighlights
  };

  return (
    <UserProgressContext.Provider value={value}>
      {children}
    </UserProgressContext.Provider>
  );
};
